import { useRef, useCallback, useEffect } from 'react';

type DebouncedFunction<T> = ((value: T) => void) & {
    cancel: () => void;
    flush: () => void;
};

export function useSimpleDebounce<T>(
    callback: (value: T) => void,
    delay: number,
): DebouncedFunction<T> {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastValueRef = useRef<T | null>(null);
    const callbackRef = useRef<(value: T) => void>(callback);

    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    const cancel = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        lastValueRef.current = null;
    }, []);

    const flush = useCallback(() => {
        if (timeoutRef.current && lastValueRef.current !== null) {
            clearTimeout(timeoutRef.current);
            const v = lastValueRef.current as T;
            timeoutRef.current = null;
            lastValueRef.current = null;
            callbackRef.current(v);
        }
    }, []);

    const debouncedCallback = useCallback(
        (value: T) => {
            lastValueRef.current = value;

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                const v = lastValueRef.current as T;
                lastValueRef.current = null;
                timeoutRef.current = null;
                callbackRef.current(v);
            }, delay);
        },
        [delay],
    );

    const fn = debouncedCallback as DebouncedFunction<T>;
    fn.cancel = cancel;
    fn.flush = flush;

    return fn;
}
